// One row of the pricing matrix. Two visual variants:
//   - 'product-header': non-editable label band introducing an option-bearing
//     product. The rate cells are blank.
//   - 'product' / 'option': priceable, with editable cells matching the section
//     strategy (one PricingRateCell per area bucket for tiered, a single flat
//     price cell for flat).
// Inactive rows render at reduced opacity so the global pill remains the only
// per-row status surface needed.
import {
  TableRow, TableCell, Box, Typography,
} from '@mui/material';
import PricingRateCell from './PricingRateCell.jsx';
import { AREA_BUCKETS, bucketLabel } from '../../../config/pricing-buckets.js';
import { PRICING_STRATEGIES } from '../../../config/pricing-strategies.js';

const PricingTargetRow = ({ row, strategy = PRICING_STRATEGIES.TIERED }) => {
  const isHeader = row.kind === 'product-header';
  const label = row.kind === 'option' ? row.optionName : row.productName;
  const indent = row.kind === 'option' ? 4 : 0;
  const labelColor = row.isActive ? 'text.primary' : 'text.disabled';
  const isFlat = strategy === PRICING_STRATEGIES.FLAT;
  const isCustomQuote = strategy === PRICING_STRATEGIES.CUSTOM_QUOTE;

  return (
    <TableRow hover sx={{ opacity: row.isActive ? 1 : 0.6 }}>
      <TableCell sx={{ pl: 2 + indent, minWidth: 280 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {isHeader ? (
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: labelColor }}>
              {row.productName}
              {row.selectionMode && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1, fontWeight: 400 }}
                >
                  {`· ${row.selectionMode}`}
                </Typography>
              )}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: labelColor }}>
              {label}
            </Typography>
          )}
        </Box>
      </TableCell>
      {isCustomQuote && (
        <TableCell align="right" sx={{ p: 0.5 }}>
          {!isHeader && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pr: 1 }}>
              Quoted manually
            </Typography>
          )}
        </TableCell>
      )}
      {!isCustomQuote && isFlat && (
        <TableCell align="right" sx={{ p: 0.5 }}>
          {!isHeader && (
            <PricingRateCell
              rowKey={row.key}
              cellIndex={0}
              savedRate={row.amount}
              ariaLabel={`Flat price for ${label}`}
            />
          )}
        </TableCell>
      )}
      {!isCustomQuote && !isFlat && AREA_BUCKETS.map((bucket, cellIndex) => (
        <TableCell key={bucket.min} align="right" sx={{ p: 0.5 }}>
          {!isHeader && (
            <PricingRateCell
              rowKey={row.key}
              cellIndex={cellIndex}
              savedRate={row.tiers?.[cellIndex]?.rate ?? 0}
              ariaLabel={`Rate for ${label}, ${bucketLabel(bucket)} sq mi`}
            />
          )}
        </TableCell>
      ))}
    </TableRow>
  );
};

export default PricingTargetRow;
